import React from 'react';
import { NavigationItem } from '@/types/contentful';
import ChevronDownIcon from '../Icons/ChevronDown';
import ContentfulLink from '../ContentfulLink/ContentfulLink';
import { MenuDropdownProps } from './types';

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  items,
  isOpen,
  onClose,
  handleClick,
  toggleLabel,
  leftOffset,
}) => {
  const safeLeftOffset = Number.isFinite(leftOffset) ? leftOffset : 0;

  const getNestedPaddingClass = (level: number): string => {
    if (level === 3) return 'pl-[5px]';
    if (level === 4) return 'pl-[10px]';
    return '';
  };

  const renderNestedItems = (
    navigationItems: NavigationItem[],
    level: number,
    parentPath: string,
  ): React.ReactNode => {
    if (level > 4) return null;

    return navigationItems.map((item, index) => {
      const paddingClass = getNestedPaddingClass(level);
      const nodePath = `${parentPath}-${item.id || `index-${index}`}`;

      if (item.__typename === 'Link') {
        return (
          <div
            key={`${nodePath}-link`}
            className={paddingClass}
          >
            <ContentfulLink
              data={{
                ...item,
                priority: item.priority,
                url: item.url || '#',
                context: 'menu',
                className: 'text-primary-500 heading-5 font-normal',
              }}
            />
          </div>
        );
      }

      return (
        <div
          key={`${nodePath}-menu`}
          className='flex flex-col gap-4'
        >
          <span className={`text-primary-500 heading-6 font-semibold ${paddingClass}`}>
            {item.menuName || item.title}
          </span>
          <div className='flex flex-col gap-4'>
            {renderNestedItems(item.items, level + 1, nodePath)}
          </div>
        </div>
      );
    });
  };

  const renderSecondLevelColumn = (item: NavigationItem, index: number): React.ReactNode => {
    if (item.__typename === 'Link') {
      return (
        <div
          key={`${item.id || 'item'}-${index}-column`}
          className='flex min-w-[16rem] flex-col gap-4'
          onClickCapture={onClose}
        >
          <ContentfulLink
            data={{
              ...item,
              priority: item.priority,
              url: item.url || '#',
              context: 'menu',
              className: 'text-primary-500 heading-5 font-normal',
            }}
          />
        </div>
      );
    }

    return (
      <div
        key={`${item.id || 'item'}-${index}-column`}
        className='flex min-w-[16rem] flex-col gap-4'
      >
        <span className='text-primary-500 heading-6 font-semibold'>
          {item.menuName || item.title}
        </span>
        {renderNestedItems(item.items, 3, item.id || `index-${index}`)}
      </div>
    );
  };

  return (
    <React.Fragment>
      <button
        className={`heading-6 flex items-center gap-4 cursor-pointer text-primary-500 font-semibold relative py-4 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-brand-accent after:transition-transform after:duration-300 ${isOpen ? 'after:scale-x-100' : 'after:scale-x-0'}`}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? 'Close' : 'Open'} ${toggleLabel}`}
        onClick={handleClick}
      >
        <span>{toggleLabel}</span>
        <ChevronDownIcon
          className={`text-brand-icon w-3 h-2 mt-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`absolute left-0 top-full mt-[0.5px] w-full transition-all duration-200 ${
          isOpen ? 'visible block opacity-100' : 'invisible hidden opacity-0'
        }`}
      >
        <div className='flex w-full bg-white py-[3.438rem] shadow-lg'>
          <div style={{ width: `${safeLeftOffset}px` }} />
          <div className='basis-1/2 2xl:basis-[60%]'>
            <div className='grid grid-cols-2 2xl:grid-cols-3 gap-x-10 gap-y-6'>
              {items.map((item, index) => renderSecondLevelColumn(item, index))}
            </div>
          </div>
        </div>
        {/* Backdrop overlay */}
        <div
          className='h-screen bg-gray-800 opacity-50 cursor-pointer'
          onClick={onClose}
        />
      </div>
    </React.Fragment>
  );
};

export default MenuDropdown;
